import React, { useEffect, useState } from 'react'
import { Box, Divider, styled, Switch, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Styles } from '../../types'
import { tokens } from '../../styles/materialTheme'
import { AnalyticEvent } from '../../analytics/adobe/types'

const analyticEventType: AnalyticEvent = 'Toggle_Click'

interface EmailPreferenceProps {
  preferenceName: string
  preferenceHeader: string
  preferenceDescription: string
  defaultPreferenceValue: boolean
  hasErrors: boolean
  onPreferenceChange: (value: boolean) => void
}

const styles: Styles = {
  preferenceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    alignItems: 'center',
  },
  preferenceDescription: {
    color: tokens.colors.rsmGray.dividers,
    paddingTop: '0.25rem',
  },
  switchContainer: {
    padding: '0 1.0625rem',
    lineHeight: 0,
    display: 'flex',
    alignItems: 'center',
  },
}

const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: '2.25rem',
  height: '1.375rem',
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '.125rem',
    '&.Mui-checked': {
      transform: 'translateX(1rem)',
      color: tokens.colors.white,
      '& + .MuiSwitch-track': {
        backgroundColor: tokens.colors.rsmBlue.primary,
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: tokens.colors.white,
  },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2,
    backgroundColor: tokens.colors.rsmGray.dividers,
    opacity: '.0625rem',
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}))

const EmailPreference = ({
  preferenceName,
  preferenceHeader,
  preferenceDescription,
  defaultPreferenceValue,
  hasErrors = false,
  onPreferenceChange,
}: EmailPreferenceProps) => {
  const { t } = useTranslation()
  const [preferenceChecked, setPreferenceChecked] = useState(true)

  useEffect(() => {
    setPreferenceChecked(defaultPreferenceValue)
  }, [defaultPreferenceValue])

  useEffect(() => {
    if (hasErrors) {
      setPreferenceChecked(!preferenceChecked)
    }
  }, [hasErrors])

  return (
    <>
      <Divider />
      <Box id={preferenceName} sx={styles.preferenceContainer}>
        <Box sx={styles.Updates}>
          {t(preferenceHeader)}
          <Typography sx={styles.preferenceDescription}>
            {t(preferenceDescription)}
          </Typography>
        </Box>
        <Box sx={styles.switchContainer}>
          <CustomSwitch
            data-testid={`Btn_EmailPreference_Switch_${preferenceName}`}
            data-analytics-event={analyticEventType}
            checked={preferenceChecked}
            onChange={() => {
              setPreferenceChecked(!preferenceChecked)
              onPreferenceChange(!preferenceChecked)
            }}
          />
        </Box>
      </Box>
      <Divider />
    </>
  )
}

export default EmailPreference
